import React, {useState} from 'react';
import {connect, DispatchProp} from "react-redux";
import {useTranslation, withTranslation} from "react-i18next";
import {ReactComponent as RemoveIco} from "../../../images/icons/remove.svg";
import {ReactComponent as BogoOutlineIco} from "../../../images/icons/bogo-outline.svg";
import {ReactComponent as InfoIco} from "../../../images/icons/info.svg";
import {RootState} from "../../../reducers";
import Money from "../Common/Money";
import PromotionInfoModalComponent from "./components/PromotionInfoModalComponent";
import {useCart} from "../../services/Cart/cartService";
import {getCart} from "../../services/Cart/reducer";
import handleException from "../../services/Api/handleException";
import {ApiError} from "../../services/Api/types";
import AppConfig from "../../services/AppConfig";

interface OwnProps {
    updateErrors: (errors: any) => void
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps & DispatchProp;
const CartPromotionsComponent = (props: Props) => {
    const {t} = useTranslation();
    const cartService = useCart();
    const cart = props.cart;
    const [loading, setLoading] = useState(false);
    const [promotionInfoModal, setPromotionInfoModal] = useState<any | undefined>(undefined);
    const sortedPromotions = cart.promotions.filter(x => x.type !== "CREDIT").sort((a, b) => a.name.localeCompare(b.name))
    const [errors, setErrors] = useState<ApiError[]>([]);

    const remove = async (orderPromotion: any) => {
        setLoading(true);
        try {
            await cartService.removePromotion(orderPromotion.id);
        } catch (e) {
            console.log(e);
        }
        setLoading(false)
    }

    const showPromotionInfo =  (promotion: any) => {
        setPromotionInfoModal(promotion);
    }

    const handlePromotionInfoClick = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, promotion: any) => {
        e.preventDefault();
        showPromotionInfo(promotion);
    }

    const handleRemoveIconClick = async (e: React.MouseEvent<HTMLDivElement | HTMLButtonElement, MouseEvent>, promotion: any) => {
        e.preventDefault();
        if(promotion.type === 'COUPON') {
            await handleDeleteCoupon();
        }
        else {
            await remove(promotion);
        }
    }

    const handleDeleteCoupon = async () => {
        try {
            await cartService.updateOrderCoupon({
                code: ""
            });
            updateErrors([]);
            setErrors([])
        } catch (error: any) {
            const errors = handleException(error);
            updateErrors(errors);
            setErrors(errors);
        }
    }

    const updateErrors = (errors: any) => {
        errors.forEach((y: any) => {
            y.field = "coupon"
        });
        props.updateErrors(errors);
    }

    const getPromotionMetadata = (promotion: any) => {
        const metadata = promotion.metadata ? promotion.metadata.replace("CODE_", "") : undefined;
        return metadata;
    }

    const isDefaultCoupon = (promotion: any) => {
        const metadata = getPromotionMetadata(promotion);

        return cart.coupon_id && cart.coupon && metadata && metadata === cart.coupon;
    }

    const isCouponAsVoucher = (promotion: any) => {
        const metadata = getPromotionMetadata(promotion);

        return !cart.coupon_id && cart.coupon && metadata && metadata === cart.coupon;
    }

    const isAutomaticPromotion = (promotion: any) => {
        return !promotion.reference_id;
    }

    const isVoucher = (promotion: any) => {
        return promotion.reference_id && promotion.reference_id.startsWith("VOUCHER");
    }

    const doesPromotionMeetTheConditions = (promotion: any) => {
        if(!(promotion.type === "ITEM_DISCOUNT" && !promotion.value_money?.amount)) return true;
        return promotion.value_money?.amount || (cart.sub_total.amount <= 0 && cart.discount_money.amount <= 0)
    }

    const drawPromotionBox = (promotion: any) => {
        const metadata = getPromotionMetadata(promotion);
        let title: string | JSX.Element = "";
        let description: string | JSX.Element = "";
        let isInactive: boolean = false;

        if(isDefaultCoupon(promotion)) {
            title =  <>{promotion.value_money.amount ? "-" : ""}<Money value={promotion.value_money.amount } currency={promotion.value_money.currency}/></>;
            description = `${promotion.name} / ${metadata}`;
        }
        else if (isCouponAsVoucher(promotion)) {
            if(promotion.type === 'VOUCHER_CREDIT') {
                const maxValueMoney = promotion.max_value_money?.amount === promotion.value_money?.amount ? undefined : promotion.max_value_money?.amount;
                title =  <>
                    {promotion.value_money.amount ? "-" : ""}<Money value={promotion.value_money.amount } currency={promotion.value_money.currency}/>
                    {maxValueMoney && maxValueMoney > 0 && (
                        <small className="text-muted d-inline-block ms-1">
                            ({t("modules.promotion.field.available.title")} <Money value={maxValueMoney} currency={promotion.max_value_money.currency}/>)
                        </small>
                    )}
                </>;
                description = `${promotion.name} / ${metadata}`;
            }
            else {
                const promotionValue = promotion.value_money?.amount ? promotion.value_money.amount : undefined;
                title = promotionValue ? <>{promotion.value_money.amount ? "-" : ""}<Money value={promotion.value_money.amount } currency={promotion.value_money.currency}/></> : promotion.name;
                description = promotionValue ? `${promotion.name} / ${metadata}` : metadata;
                if (!doesPromotionMeetTheConditions(promotion)) {
                    isInactive = true;
                }
            }
        }
        else if (isAutomaticPromotion(promotion)) {
            title = <>{promotion.value_money.amount ? "-" : ""}<Money value={promotion.value_money.amount } currency={promotion.value_money.currency}/></>;
            description  = promotion.name;
        }
        else if (isVoucher(promotion)) {
            if (promotion.type === "VOUCHER_CREDIT") {
                const maxValueMoney = promotion.max_value_money?.amount === promotion.value_money?.amount ? undefined : promotion.max_value_money?.amount;
                title =  <>
                    {promotion.value_money.amount ? "-" : ""}<Money value={promotion.value_money.amount } currency={promotion.value_money.currency}/>
                    {maxValueMoney && maxValueMoney > 0 && (
                        <small className="text-muted d-inline-block ms-1">
                            ({t("modules.promotion.field.available.title")} <Money value={maxValueMoney} currency={promotion.max_value_money.currency}/>)
                        </small>
                    )}
                </>;
                description = promotion.name;
            }
            else {
                const promotionValue = promotion.value_money?.amount ? promotion.value_money.amount : undefined;
                title = promotionValue ? <>{promotion.value_money.amount ? "-" : ""}<Money value={promotion.value_money.amount } currency={promotion.value_money.currency}/></> : promotion.name;
                description = promotionValue ? `${promotion.name}${metadata ? ` / ${metadata}` : ""}` : metadata;
                if (!doesPromotionMeetTheConditions(promotion)) {
                    isInactive = true;
                }
            }
        }

        return (
            <>
                <div className={"btn btn-outline-light btn-promotion"}>
                    <div className={"content d-flex align-items-center"}>
                        <BogoOutlineIco className={`${isInactive ? "text-muted" : "text-success"} me-2`}/>
                        <div className="d-flex flex-column">
                            <p className={`mb-0 fw-normal ${isInactive ? "text-muted" : "text-success"}`}>{title}</p>
                            <small className="text-muted text-start fw-normal">{description}</small>
                        </div>
                    </div>
                    <div className="d-flex align-items-center gap-2 ms-auto">
                        {
                            isInfoButtonVisible(promotion) && (
                                <button className={"btn btn-outline-secondary btn-icon btn-spinner"} onClick={(e) => handlePromotionInfoClick(e, promotion)}>
                                    <InfoIco />
                                </button>
                            )
                        }
                        {isRemoveButtonVisible(promotion) && (
                            <button className={`btn btn-outline-danger btn-icon btn-spinner ${loading ? " loading" : ""}`} onClick={(e) => handleRemoveIconClick(e, promotion)}>
                                <span className="left spinner-border spinner-border-sm"/>
                                <RemoveIco />
                            </button>
                        )}
                    </div>
                </div>
                {
                    isInactive && (
                        <small className={"text-danger fw-normal"}>
                            {t("modules.promotion.field.not_meet_promotion_condition.title")}
                        </small>
                    )
                }
       </>
        )
    }

    const isRemoveButtonVisible = (promotion: any) => {
        if(isDefaultCoupon(promotion) || isCouponAsVoucher(promotion) || isVoucher(promotion)) return true;

        return false;
    }

    const isInfoButtonVisible = (promotion: any) => {
        if(isDefaultCoupon(promotion) && promotion.type !== "COUPON") return true;

        if(isCouponAsVoucher(promotion) && promotion.type !== 'VOUCHER_CREDIT')  return true;

        if(isAutomaticPromotion(promotion)) return true;

        if(isVoucher(promotion) && promotion.type !== 'VOUCHER_CREDIT') return true;

        return false;
    }

    if(sortedPromotions.length == 0) return <></>;

    return (
        <>
            {promotionInfoModal && (
                <PromotionInfoModalComponent name={promotionInfoModal.name}
                                             promotionId={promotionInfoModal.promotion_id}
                                             onHide={() => setPromotionInfoModal(undefined)} onShow={() => {
                }}/>
            )}
            <div className={"cart-promotions gap-2 row mb-3"}>
                {sortedPromotions.map((promotion) => {
                    if (promotion.type == "TIP") return null;

                    return (
                        <div key={promotion.id} className="col-md-6 promotion-box">
                            {drawPromotionBox(promotion)}
                        </div>
                    )
                })}
            </div>
        </>
    )

}


const mapStateToProps = (state: RootState) => ({
    cart: getCart(state.cart)
})

const connector = connect(mapStateToProps);
export default withTranslation()(connector(CartPromotionsComponent));